import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import SaveIcon from "@mui/icons-material/Save";
import { Typography } from "@mui/material";
import type { FC } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useSelfScheduling } from "@/contexts/SelfScheduling/SelfSchedulingContext";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import SystemDialog from "@/ui/Popups/Dialog";
import SystemButton from "@/ui/SystemButton/SystemButton";

export const SelfSchedulingButton: FC = () => {
	const { t } = useTranslation();
	const { isImportingSelfScheduling, toggleImportingSelfScheduling, isLoading } = useSelfScheduling();
	const { chosenDepartment } = useHospitalData();
	const selfSchedulingEnabled = useFeatureFlag(FEATURE_FLAGS.SelfSchedulingEnabled);
	const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

	const dateRange = useMemo(() => {
		if (!chosenDepartment.selfScheduling) return { periodStart: "", periodEnd: "" };

		return {
			periodStart: chosenDepartment.selfScheduling.periodStart.toFormat("yyyy-MM-dd"),
			periodEnd: chosenDepartment.selfScheduling.periodEnd.toFormat("yyyy-MM-dd"),
		};
	}, [chosenDepartment.selfScheduling]);

	return (
		selfSchedulingEnabled && (
			<>
				<SystemButton
					buttonVariant="secondary"
					size="small"
					tooltip={isImportingSelfScheduling ? t("publish.shifts") : t("import.self-scheduling")}
					onClick={() => setShowConfirmDialog(true)}
					sx={{ border: "none", bgcolor: "transparent", p: 2, color: "primary.main" }}
					leadingIcon={
						isImportingSelfScheduling ? (
							<SaveIcon fontSize="small" sx={{ color: "primary.main" }} />
						) : (
							<CalendarMonthOutlinedIcon fontSize="small" sx={{ color: "primary.main" }} />
						)
					}
				/>
				<SystemDialog
					open={showConfirmDialog}
					onClose={() => setShowConfirmDialog(false)}
					title={isImportingSelfScheduling ? t("publish.self-scheduled.shifts") : t("import.self-scheduling")}
					body={
						isImportingSelfScheduling ? (
							t("publish.self-scheduled.shifts.to.staff", dateRange)
						) : (
							<>
								<Typography>{t("importing.staff.preferences.locks.schedule", dateRange)}</Typography>
								<Typography>{t("importing.staff.preferences.locks.schedule.warning")}</Typography>
							</>
						)
					}
					onConfirm={async () => {
						await toggleImportingSelfScheduling();
						setShowConfirmDialog(false);
					}}
					confirm={isImportingSelfScheduling ? t("publish") : t("import.and.lock")}
					cancel={t("cancel")}
					confirmDisabled={isLoading}
				/>
			</>
		)
	);
};
